import { Feature, UserSubscriptionAction } from "common/types";
import { toDate } from "common/utils/dates";

import ConfirmationDialog from "client/common/components/content/ConfirmationDialog";
import { useCurrentDialog } from "client/common/hooks/stackables";
import { useUpdateSubscriptionMutation } from "client/subscriptions/hooks";
import { useFeatures } from "client/clinics/hooks";
import { SubscriptionPaymentUpdateResponse } from "client/subscriptions/types";
import CardPaymentDeclineAlert from "client/common/components/content/CardPaymentDeclineAlert";

export type SubscriptionPaymentUpdateConfirmationDialogProps = {
  subscription: SubscriptionPaymentUpdateResponse;
};

const SubscriptionPaymentUpdateConfirmationDialog = ({
  subscription,
}: SubscriptionPaymentUpdateConfirmationDialogProps) => {
  const { close } = useCurrentDialog();
  const isEdbEnabled = useFeatures()[Feature.AutoshipEDB];

  const {
    mutate: updateSubscription,
    isLoading: isUpdating,
    error: subscriptionUpdateError,
  } = useUpdateSubscriptionMutation(subscription.id, {
    onSuccess: close,
  });

  const getScheduleDescription = () => {
    if (isEdbEnabled) {
      return subscription.expected_delivery_by
        ? `Would you like to place an order now or wait until your next estimated delivery on ${toDate(
            subscription.expected_delivery_by,
          )}?`
        : "Would you like to place an order now or wait until your next estimated delivery?";
    }
    return subscription.next_order_date
      ? `Would you like to place an order now or wait until your next scheduled Autoship on ${toDate(
          subscription.next_order_date,
        )}?`
      : "Would you like to place an order now or wait until your next scheduled Autoship?";
  };

  return (
    <ConfirmationDialog
      title="Schedule your order"
      description={getScheduleDescription()}
      denyText="No, I'll wait"
      confirmText="Yes, order now"
      onConfirm={() =>
        updateSubscription({
          action: UserSubscriptionAction.OrderNow,
        })
      }
      onDeny={close}
      isLoading={isUpdating}
      error={subscriptionUpdateError}
      renderError={(error) => {
        const cardError = error?.data?.card_decline_info;
        return cardError && <CardPaymentDeclineAlert declineError={cardError} />;
      }}
    />
  );
};

export default SubscriptionPaymentUpdateConfirmationDialog;
